import { useEffect, useRef, useState } from 'react';
import { GraphCard } from '../../../SharedComponents';
import {
  Alert,
  Box,
  ButtonGroup,
  Icon,
  IconButton,
  Label,
  LabeledControl,
  Modal,
  Paragraph,
  Radio,
  Spinner,
  ToggleButton,
} from '@zillow/constellation-10';
import {
  IconCalendarFilled,
  IconChevronLeftOutline,
  IconChevronRightOutline,
  IconCloseFilled,
  IconDownloadFilled,
  IconExpandFilled,
} from '@zillow/constellation-icons';
import { useMainLayout } from '../../../layouts/MainPageLayout/MainLayoutContext';
import { localDateToDate } from '../../../../utils/localDateToDate';
import { addMonths, format, startOfMonth, subQuarters, subYears } from 'date-fns';
import { makeTableConfig, type MonthlyPropertyStats, type StatType, TableType } from './AdSpendReport.config';
import {
  downloadAdspendExcel,
  downloadAdspendExcelMultiSheet,
  filterStatsForBoost,
  filterStatsForExtend,
} from './AdSpendReport.utils';
import { AdSpendAdvancedTable } from './AdSpendReportAdvancedTable';
import { allTimestampsOneYear } from '../../../../utils/allTimestampsOneYear';
import React from 'react';
import { PackageTypes } from '../../../../utils/packageTypes';
import { mapPackageTypeName } from '../../../../utils/mapPackageTypeName';
import { ClientAdProductType, ClientCpmBoostType } from '@zg-rentals/ts-customer-analytics-client';
import { usePortalAnalyticsWebApiHandler_getSfhAccountLevelSpend } from '@zg-rentals/ts-customer-analytics-client-react-query';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import {
  emitAdSpendTableDownloadEvent,
  emitAdSpendTableFullscreenEvent,
  emitAdSpendTablePackageFilterEvent,
} from '../../../../analytics/events';

export type PackageFilter = ClientAdProductType | 'ALL';

const AdspendTableChild = ({
  tableType,
  setTableType,
  selectedMonthStats,
  lastMonthStats,
  firstMonthStats,
  monthlyStatsByMonthAndYear,
  setSelectedMonthStats,
  isFullscreen,
  setIsFullscreen,
  handleDownloadExcel,
  displayBoostStandAlone,
  displayExtend,
  selectedPackageFilter,
  setSelectedPackageFilter,
  availablePackageTypes,
  instanceId,
  user,
}: {
  tableType: TableType;
  setTableType: (tableType: TableType) => void;
  selectedMonthStats: MonthlyPropertyStats | null;
  lastMonthStats: MonthlyPropertyStats | null;
  firstMonthStats: MonthlyPropertyStats | null;
  monthlyStatsByMonthAndYear: Array<MonthlyPropertyStats>;
  setSelectedMonthStats: (selectedMonthStats: MonthlyPropertyStats | null) => void;
  isFullscreen: boolean;
  setIsFullscreen: (isFullscreen: boolean) => void;
  handleDownloadExcel: () => void;
  displayBoostStandAlone: boolean;
  displayExtend: boolean;
  selectedPackageFilter: PackageFilter;
  setSelectedPackageFilter: (filter: PackageFilter) => void;
  availablePackageTypes: Array<ClientAdProductType>;
  instanceId: string;
  user: ReturnType<typeof useRentalManagerUser>['user'];
}) => {
  const printCardRef = useRef<HTMLDivElement>(null);

  return (
    <Box ref={printCardRef}>
      {/* Top row: Tabs and action buttons */}
      <Box
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBlockEnd: '400',
          gap: '400',
          flexWrap: 'wrap',
        }}
      >
        <Box css={{ display: 'flex', alignItems: 'center', gap: '400' }}>
          <ButtonGroup aria-label="toggle layout" conjoined orientation="horizontal" role="group">
            <ToggleButton
              value={TableType.ALL}
              selected={tableType === TableType.ALL}
              onClick={() => setTableType(TableType.ALL)}
              css={{ whiteSpace: 'nowrap' }}
            >
              All spend
            </ToggleButton>
            <ToggleButton
              value={TableType.PACKAGES}
              selected={tableType === TableType.PACKAGES}
              onClick={() => setTableType(TableType.PACKAGES)}
            >
              Packages
            </ToggleButton>
            {displayBoostStandAlone && (
              <ToggleButton
                value={TableType.BOOST}
                selected={tableType === TableType.BOOST}
                onClick={() => setTableType(TableType.BOOST)}
              >
                Boost
              </ToggleButton>
            )}
            {displayExtend && (
              <ToggleButton
                value={TableType.EXTEND}
                selected={tableType === TableType.EXTEND}
                onClick={() => setTableType(TableType.EXTEND)}
              >
                Extend
              </ToggleButton>
            )}
          </ButtonGroup>
        </Box>
        <Box css={{ display: 'flex', alignItems: 'center', gap: '300' }}>
          {/*           <IconButton
            emphasis="outlined"
            shape="square"
            size="sm"
            title="print"
            tone="neutral"
            onClick={() => {
              if (user) emitAdSpendTablePrintEvent({ user });
              window.print();
            }}
          >
            <Icon>
              <IconPrintFilled />
            </Icon>
          </IconButton> */}
          <IconButton
            emphasis="outlined"
            shape="square"
            size="sm"
            title="download"
            tone="neutral"
            onClick={() => {
              if (user) emitAdSpendTableDownloadEvent({ user });
              handleDownloadExcel();
            }}
          >
            <Icon>
              <IconDownloadFilled />
            </Icon>
          </IconButton>
          <IconButton
            emphasis="outlined"
            shape="square"
            size="sm"
            title="fullscreen"
            tone="neutral"
            onClick={() => {
              if (user) emitAdSpendTableFullscreenEvent({ user });
              setIsFullscreen(!isFullscreen);
            }}
          >
            <Icon>{isFullscreen ? <IconCloseFilled /> : <IconExpandFilled />}</Icon>
          </IconButton>
        </Box>
      </Box>

      {/* Second row: Date navigation and package filter */}
      <Box
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBlockEnd: '800',
          gap: '400',
          flexWrap: 'wrap',
        }}
      >
        <Box css={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            emphasis="bare"
            shape="square"
            size="md"
            title="previous"
            tone="neutral"
            css={{ width: 'min-content', marginInlineEnd: '200' }}
            disabled={selectedMonthStats?.monthAndYear === lastMonthStats?.monthAndYear}
            onClick={() => {
              const index = monthlyStatsByMonthAndYear.findIndex(
                (item) => item.monthAndYear === selectedMonthStats?.monthAndYear,
              );
              if (index >= 0) {
                setSelectedMonthStats(monthlyStatsByMonthAndYear[index + 1]);
              }
            }}
          >
            <Icon>
              <IconChevronLeftOutline />
            </Icon>
          </IconButton>
          <Box asChild>
            <span>
              {selectedMonthStats?.monthAndYear}&nbsp;
              <Icon size="md" css={{ verticalAlign: 'middle', position: 'relative', top: '-3px' }}>
                <IconCalendarFilled />
              </Icon>
            </span>
          </Box>
          <IconButton
            emphasis="bare"
            shape="square"
            size="md"
            title="next"
            tone="neutral"
            css={{ width: 'min-content', marginInlineStart: '200' }}
            disabled={selectedMonthStats?.monthAndYear === firstMonthStats?.monthAndYear}
            onClick={() => {
              const index = monthlyStatsByMonthAndYear.findIndex(
                (item) => item.monthAndYear === selectedMonthStats?.monthAndYear,
              );
              if (index >= 0) {
                setSelectedMonthStats(monthlyStatsByMonthAndYear[index - 1]);
              }
            }}
          >
            <Icon>
              <IconChevronRightOutline />
            </Icon>
          </IconButton>
        </Box>
        {(tableType === TableType.ALL || tableType === TableType.PACKAGES) && (
          <Box css={{ display: 'flex', alignItems: 'center', gap: '400', flexWrap: 'wrap' }}>
            <LabeledControl
              key={`${instanceId}-ALL`}
              control={
                <Radio
                  checked={selectedPackageFilter === 'ALL'}
                  name={`package-filter-${instanceId}`}
                  onChange={() => {
                    if (user) emitAdSpendTablePackageFilterEvent({ user });
                    setSelectedPackageFilter('ALL');
                  }}
                  value="ALL"
                />
              }
              label={<Label>All</Label>}
            />
            {availablePackageTypes.map((packageType) => {
              const packageName = mapPackageTypeName(packageType);
              return (
                <LabeledControl
                  key={`${instanceId}-${packageType}`}
                  control={
                    <Radio
                      checked={selectedPackageFilter === packageType}
                      name={`package-filter-${instanceId}`}
                      onChange={() => {
                        if (user) emitAdSpendTablePackageFilterEvent({ user });
                        setSelectedPackageFilter(packageType);
                      }}
                      value={packageType}
                    />
                  }
                  label={<Label>{packageName}</Label>}
                />
              );
            })}
          </Box>
        )}
        {tableType === TableType.BOOST && (
          <Paragraph css={{ color: 'text.secondary', textAlign: 'right' }}>
            The displayed performance data is only for Boost, and excludes other spend.
          </Paragraph>
        )}
        {tableType === TableType.EXTEND && (
          <Paragraph css={{ color: 'text.secondary', textAlign: 'right' }}>
            The displayed performance data is only for Extend, and excludes other spend. This service will appear on
            your invoice as Package Media Add-On.
          </Paragraph>
        )}
      </Box>
      {selectedMonthStats && selectedMonthStats.stats.length > 0 ? (
        <AdSpendAdvancedTable
          tableType={tableType}
          monthlyStatsByMonthAndYear={monthlyStatsByMonthAndYear}
          selectedMonthStats={selectedMonthStats}
          packageFilter={selectedPackageFilter}
        />
      ) : (
        <Alert tone="info">No data available</Alert>
      )}
    </Box>
  );
};

export const AdSpendTable = () => {
  const [tableType, setTableType] = useState<TableType>(TableType.ALL);
  const { filteredProperties, isFetchingInitialData } = useMainLayout();
  const { user } = useRentalManagerUser();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [monthlyStatsByMonthAndYear, setMonthlyStatsByMonthAndYear] = useState<Array<MonthlyPropertyStats>>([]);
  const [selectedMonthStats, setSelectedMonthStats] = useState<MonthlyPropertyStats | null>(null);
  const [selectedPackageFilter, setSelectedPackageFilter] = useState<PackageFilter>('ALL');

  // Fetch SFH account level spend data
  const today = new Date();
  const oneYearAgo = startOfMonth(addMonths(subYears(today, 1), 1));
  const oneYearAndOneQuarterAgo = startOfMonth(subQuarters(oneYearAgo, 1));

  const { data: sfhAccountLevelSpendData } = usePortalAnalyticsWebApiHandler_getSfhAccountLevelSpend({
    startDate: {
      year: oneYearAndOneQuarterAgo.getFullYear(),
      month: oneYearAndOneQuarterAgo.getMonth() + 1,
      day: oneYearAndOneQuarterAgo.getDate(),
    },
    endDate: {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDate(),
    },
  });

  // Get unique package types from current month's data where at least 1 property has that type
  const availablePackageTypes = React.useMemo(() => {
    if (!selectedMonthStats || !selectedMonthStats.stats || selectedMonthStats.stats.length === 0) {
      return [];
    }

    const packageTypesSet = new Set<ClientAdProductType>();
    // Collect all package types that exist in the current month's data
    selectedMonthStats.stats.forEach((stat) => {
      if (stat.adproductTypes && stat.adproductTypes.length > 0) {
        stat.adproductTypes.forEach((type) => {
          if (type) {
            packageTypesSet.add(type);
          }
        });
      }
    });

    // Sort package types in the standard order
    const sortOrder: Array<ClientAdProductType> = [
      ClientAdProductType.BASE_PACKAGE,
      ClientAdProductType.ENHANCED_PACKAGE,
      ClientAdProductType.PREMIUM_PACKAGE,
      ClientAdProductType.PREMIUM_PLUS_PACKAGE,
      ClientAdProductType.SIGNATURE_PACKAGE,
    ];

    const sorted = Array.from(packageTypesSet).sort((a, b) => {
      return sortOrder.indexOf(a) - sortOrder.indexOf(b);
    });

    return sorted;
  }, [selectedMonthStats]);

  // Reset filter to 'ALL' when month changes only if the current filter is not available in the new month
  React.useEffect(() => {
    if (selectedPackageFilter !== 'ALL' && !availablePackageTypes.includes(selectedPackageFilter)) {
      setSelectedPackageFilter('ALL');
    }
  }, [selectedMonthStats?.monthAndYear, selectedPackageFilter, availablePackageTypes]);

  useEffect(() => {
    const monthlyStatsByMonthAndYear: Array<MonthlyPropertyStats> = [];
    const timestamps = allTimestampsOneYear();
    timestamps.forEach((timestamp) => {
      const formattedDate = format(new Date(timestamp), 'MMMM yyyy');
      monthlyStatsByMonthAndYear.push({
        stats: [],
        monthAndYear: formattedDate,
      });
    });

    filteredProperties.forEach((property) => {
      property.breakdownStats.forEach((stat) => {
        const date = localDateToDate(stat.statDate);
        const monthAndYear = format(date, 'MMMM yyyy');
        const packageType = stat.adproductTypes[0];
        if (packageType) {
          if (!monthlyStatsByMonthAndYear.some((item) => item.monthAndYear === monthAndYear)) {
            monthlyStatsByMonthAndYear.push({
              stats: [{ ...stat, propertyDetails: property.propertyDetails }],
              monthAndYear,
            });
          } else {
            monthlyStatsByMonthAndYear
              .find((item) => item.monthAndYear === monthAndYear)
              ?.stats.push({ ...stat, propertyDetails: property.propertyDetails });
          }
        }
      });
    });

    monthlyStatsByMonthAndYear.sort((a, b) => new Date(b.monthAndYear).getTime() - new Date(a.monthAndYear).getTime());

    setMonthlyStatsByMonthAndYear(monthlyStatsByMonthAndYear);
    if (monthlyStatsByMonthAndYear.length > 0) {
      setSelectedMonthStats(monthlyStatsByMonthAndYear[0]);
    }
  }, [filteredProperties]);

  // Check for MFH standalone boost
  const hasMfhStandAloneBoost = React.useMemo(() => {
    if (!selectedMonthStats) {
      return false;
    }
    return selectedMonthStats?.stats.some((stat) => {
      if (stat.adproductTypes.some((type) => PackageTypes.has(type))) {
        return false;
      }
      return stat.boostCost > 0;
    });
  }, [selectedMonthStats]);

  // Check for SFH account level boost spend
  const hasSfhStandAloneBoost = React.useMemo(() => {
    if (!sfhAccountLevelSpendData || sfhAccountLevelSpendData.length === 0) {
      return false;
    }
    return sfhAccountLevelSpendData.some((monthData) => {
      return monthData.totalBoostSpend > 0;
    });
  }, [sfhAccountLevelSpendData]);

  // Display boost standalone tab if either MFH or SFH has boost spend
  const displayBoostStandAlone = hasMfhStandAloneBoost || hasSfhStandAloneBoost;

  const displayExtend = React.useMemo(() => {
    if (!selectedMonthStats) {
      return false;
    }
    return selectedMonthStats.stats.some(
      (stat) => stat.addOnBoostCost > 0 || stat.addOnImpressions > 0 || stat.addOnClickCount > 0,
    );
  }, [selectedMonthStats]);

  // Create virtual rows from SFH account level spend data
  const sfhVirtualRows = React.useMemo(() => {
    if (!sfhAccountLevelSpendData || sfhAccountLevelSpendData.length === 0) {
      return [];
    }

    const virtualRowsByMonth: Array<MonthlyPropertyStats> = [];

    sfhAccountLevelSpendData.forEach((monthData) => {
      if (monthData.totalBoostSpend <= 0) {
        return; // Skip months with no boost spend
      }

      const date = localDateToDate(monthData.monthDate);
      const monthAndYear = format(date, 'MMMM yyyy');

      // Create one virtual row per stat entry
      const virtualRows: Array<StatType> = (Array.isArray(monthData?.stats) ? monthData.stats : []).map((stat) => {
        // Calculate CPC and CTR for this individual stat
        const boostCpc = stat.clicks > 0 ? stat.totalCost / stat.clicks : 0;
        const boostCtr = stat.ctr;

        const cpmBoostTypes: Array<ClientCpmBoostType> = [];
        cpmBoostTypes.push(ClientCpmBoostType.ZG);

        // Create a virtual row that matches the PortfolioAnalyticsStatsDto structure
        // Using type assertion since we're creating a virtual row with placeholder values
        // Only the fields used in BOOST table type need to be accurate
        return {
          adproductTypes: [],
          advertisedUnitsAverage: 0,
          allListingFilterTypes: [],
          analyticsListingType: undefined,
          averageRentPriceByBedroom: {},
          boostClickCount: stat.clicks,
          boostCost: stat.totalCost,
          boostCpc,
          boostCtr,
          boostImpressions: stat.impressions,
          clickCount: stat.clicks,
          coreProductCost: 0,
          cpmBoostTypes,
          currentAdproductType: undefined,
          currentListingAlias: undefined,
          customerId: '',
          daysOnMarketCount: 0,
          emailLeadCount: 0,
          hasMoodysData: false,
          hasSingleFamilyBoost: true,
          impressionCount: 0,
          insertionOrderId: stat.insertionOrderId.toString(),
          isSingleFamily: true,
          leadCount: 0,
          leaseConnectCost: 0,
          leaseCount: 0,
          listingAliases: [],
          packageCost: 0,
          phoneLeadCount: 0,
          reportFrequency: undefined,
          rocketshipCost: 0,
          singleFamilyLegacyCost: 0,
          singleFamilyMonthFlatFeeCost: 0,
          statDate: monthData.monthDate,
          totalCost: stat.totalCost,
          viewCount: 0,
          zrnNumber: '',
          propertyDetails: {
            city: '',
            cityRegionId: 0,
            countyRegionId: 0,
            lat: 0,
            listingAlias: undefined,
            listingName: stat.name,
            lon: 0,
            lotId: 0,
            metroArea: '',
            metroStatRegionId: 0,
            numUnits: 0,
            specialityHousingType: undefined,
            state: '',
            stateRegionId: 0,
            street: '',
            unit: '',
            unitId: 0,
            zip: '',
            zipRegionId: 0,
          },
        } as unknown as StatType;
      });

      virtualRowsByMonth.push({
        stats: virtualRows,
        monthAndYear,
      });
    });

    return virtualRowsByMonth;
  }, [sfhAccountLevelSpendData]);

  // Combine regular rows with virtual SFH rows when tableType is BOOST
  const displayedMonthlyStats = React.useMemo(() => {
    if (tableType !== TableType.BOOST || sfhVirtualRows.length === 0) {
      return monthlyStatsByMonthAndYear;
    }

    // Merge virtual rows into the appropriate months
    const mergedStats = monthlyStatsByMonthAndYear.map((monthStats) => {
      const matchingVirtualRow = sfhVirtualRows.find(
        (virtualRow) => virtualRow.monthAndYear === monthStats.monthAndYear,
      );

      if (matchingVirtualRow) {
        return {
          ...monthStats,
          stats: [...monthStats.stats, ...matchingVirtualRow.stats],
        };
      }

      return monthStats;
    });

    return mergedStats;
  }, [tableType, monthlyStatsByMonthAndYear, sfhVirtualRows]);

  // Update selectedMonthStats when displayedMonthlyStats changes to reflect virtual rows
  React.useEffect(() => {
    if (selectedMonthStats && displayedMonthlyStats.length > 0) {
      const updatedMonthStats = displayedMonthlyStats.find(
        (stats) => stats.monthAndYear === selectedMonthStats.monthAndYear,
      );
      if (updatedMonthStats && updatedMonthStats !== selectedMonthStats) {
        setSelectedMonthStats(updatedMonthStats);
      }
    }
  }, [displayedMonthlyStats, selectedMonthStats]);

  const handleDownloadExcel = () => {
    if (!selectedMonthStats) return;

    const displayingDate = selectedMonthStats.monthAndYear.replace(' ', '_');
    const fileName = `Ad_Spend_${displayingDate}.xlsx`;
    const noOp = () => {};

    if (displayBoostStandAlone || displayExtend) {
      const allSpendMonth = monthlyStatsByMonthAndYear.find((m) => m.monthAndYear === selectedMonthStats.monthAndYear);
      const allStats = allSpendMonth?.stats ?? [];
      const allConfig = makeTableConfig({
        tableType: TableType.ALL,
        previewAd: noOp,
        onPropertyClick: noOp,
      });

      const packagesStats = allStats.filter((stat) => stat.adproductTypes.length > 0);
      const packagesConfig = makeTableConfig({ tableType: TableType.PACKAGES, previewAd: noOp, onPropertyClick: noOp });

      const sheets = [
        { sheetName: 'All Spend', stats: allStats, config: allConfig },
        { sheetName: 'Packages', stats: packagesStats, config: packagesConfig },
      ];

      if (displayBoostStandAlone) {
        const boostVirtualMonth = sfhVirtualRows.find((v) => v.monthAndYear === selectedMonthStats.monthAndYear);
        const mergedBoostStats = [...allStats, ...(boostVirtualMonth?.stats ?? [])];
        const boostStats = filterStatsForBoost(mergedBoostStats);
        const boostConfig = makeTableConfig({ tableType: TableType.BOOST, previewAd: noOp, onPropertyClick: noOp });
        sheets.push({ sheetName: 'Boost', stats: boostStats, config: boostConfig });
      }

      if (displayExtend) {
        const extendStats = filterStatsForExtend(allStats);
        const extendConfig = makeTableConfig({ tableType: TableType.EXTEND, previewAd: noOp, onPropertyClick: noOp });
        sheets.push({ sheetName: 'Extend', stats: extendStats, config: extendConfig });
      }

      downloadAdspendExcelMultiSheet(sheets, fileName);
    } else {
      const selectedConfig = makeTableConfig({ tableType, previewAd: noOp, onPropertyClick: noOp });
      downloadAdspendExcel(selectedMonthStats?.stats ?? [], selectedConfig, fileName);
    }
  };

  if (isFetchingInitialData)
    return (
      <GraphCard css={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spinner size="lg" />
      </GraphCard>
    );

  if (!isFetchingInitialData && displayedMonthlyStats.length === 0) {
    return (
      <GraphCard css={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Alert tone="info">No data available</Alert>
      </GraphCard>
    );
  }

  const lastMonthStats = displayedMonthlyStats[displayedMonthlyStats.length - 1];
  const firstMonthStats = displayedMonthlyStats[0];

  return (
    <>
      <GraphCard loading={isFetchingInitialData}>
        <AdspendTableChild
          tableType={tableType}
          setTableType={setTableType}
          selectedMonthStats={selectedMonthStats}
          lastMonthStats={lastMonthStats}
          firstMonthStats={firstMonthStats}
          monthlyStatsByMonthAndYear={displayedMonthlyStats}
          setSelectedMonthStats={setSelectedMonthStats}
          isFullscreen={isFullscreen}
          setIsFullscreen={setIsFullscreen}
          handleDownloadExcel={handleDownloadExcel}
          displayBoostStandAlone={displayBoostStandAlone}
          displayExtend={displayExtend}
          selectedPackageFilter={selectedPackageFilter}
          setSelectedPackageFilter={setSelectedPackageFilter}
          availablePackageTypes={availablePackageTypes}
          instanceId="normal"
          user={user}
        />

        <Modal
          open={isFullscreen}
          size={{ lg: 'xl', base: 'fullScreen' }}
          css={{ maxWidth: { lg: 'calc(100vw - 100px)', base: '100vw' }, padding: '0 !important' }}
          scrollDocument={false}
          header={null}
          dividers={false}
          closeButton={<span hidden />}
          onOpenChange={(open) => !open && setIsFullscreen(false)}
          body={
            <AdspendTableChild
              tableType={tableType}
              setTableType={setTableType}
              selectedMonthStats={selectedMonthStats}
              lastMonthStats={lastMonthStats}
              firstMonthStats={firstMonthStats}
              monthlyStatsByMonthAndYear={displayedMonthlyStats}
              setSelectedMonthStats={setSelectedMonthStats}
              isFullscreen={isFullscreen}
              setIsFullscreen={setIsFullscreen}
              handleDownloadExcel={handleDownloadExcel}
              displayBoostStandAlone={displayBoostStandAlone}
              displayExtend={displayExtend}
              selectedPackageFilter={selectedPackageFilter}
              setSelectedPackageFilter={setSelectedPackageFilter}
              availablePackageTypes={availablePackageTypes}
              instanceId="fullscreen"
              user={user}
            />
          }
        />
      </GraphCard>
    </>
  );
};
